import { HALL_CARDS, cardPrice, tagWords } from '@/engine/hall';
import {
  cardEffect, cardGives, cardDraws, sendsToBand, bonusesFor,
} from '@/engine/roleActions';

// One description of a card, built once and read by every place that shows
// one: the hand, the shelf, the deck list, the play panel. Gains stay as
// OBJECTS here so the card face can draw them as icons rather than hand a
// sentence to a reader who has to parse which half is which.
//
// Since playtest 14 a card has NO PLAY COST: the shelf's price is the whole
// price, and a face shows only what the card does. (Elizabeth: "it looks
// like it costs me one coin and gets me two happiness, but then underneath
// it says 'then 2 coin to play'. I don't understand what that means.")

// "+2 Tradition", not "2 toward Tradition". Matt: "the lean toward two
// towards tradition should just be like plus two tradition... this stuff all
// needs to be more mechanical, less thematic."
export const leanList = (c = {}) => [
  c.x ? `+${Math.abs(c.x)} ${c.x < 0 ? 'Tradition' : 'Novelty'}` : '',
  c.y ? `+${Math.abs(c.y)} ${c.y < 0 ? 'Order' : 'Liberty'}` : '',
].filter(Boolean);

export const pushWords = (c = {}) => leanList(c).join(', ');

// What a standing law DOES, as words on the face (playtest 13: both laws
// were bought blind - "it doesn't say what game effect it has on the card").
const LAW_WORDS = {
  marketCoin: (n) => `markets pay +${n} coin`,
  fever: (n) => `fevers ${Math.abs(n)} in 10 ${n < 0 ? 'less' : 'more'} likely`,
  hitOn: (n) => `the town's people hit on ${Math.abs(n)} better at the walls`,
};
export const lawText = (id) => Object.entries(HALL_CARDS[id]?.law || {})
  .map(([k, n]) => (LAW_WORDS[k] ? LAW_WORDS[k](n) : `${k} ${n > 0 ? '+' : ''}${n}`)).join(', ');

/** The emblem family a card draws with: its own id first, else a word for what it is. */
export const artKeyFor = (c = {}) => (c.grievance ? 'grievance' : c.law ? 'law' : (c.tags || [])[0] || 'custom');

/** The one word a counselor could like about this card, if any: "a feast". */
export const tagLine = (c = {}) => ((c.tags || []).length ? tagWords(c.tags[0]) : '');

export const faceFor = (civ, id, counselor = null) => {
  const c = HALL_CARDS[id];
  const gives = cardGives(civ, id, counselor);
  const draws = cardDraws(civ, id, counselor);
  const push = pushWords(c.compass);
  const bonuses = bonusesFor(civ, id, counselor);
  return {
    id,
    name: c.name,
    text: c.text,
    art: artKeyFor(c),
    tags: c.tags || [],
    grievance: Boolean(c.grievance),
    // Workers a card raises are a gain like any other, so they get a pip
    // rather than a sentence: a Levy with nothing on its bottom edge read
    // as a card that did nothing.
    gain: { ...cardEffect(civ, id, counselor), ...(gives ? { workers: gives } : {}) },
    price: cardPrice(id) || {},
    bounty: c.bounty || {},
    // Only the short, surprising things go on the face. The compass push
    // and "to a role you name" live in the panel you get when you pick the
    // card up: on a card 100px wide they were two lines of clipped text.
    extras: [
      draws ? `draw ${draws}` : '',
      // Workers that can only go to the Frontier say so ON THE CARD (Matt,
      // playtest 9: Call to Arms "isn't really clear from any of the icons
      // on the card itself; it just shows the same +2 workers icon").
      gives && sendsToBand(civ, id, counselor) ? 'Frontier only' : '',
      // Workers for TODAY, not new people (playtest 11: Nick read a Levy's
      // +2 workers as citizens and was surprised it cost happiness).
      gives && !sendsToBand(civ, id, counselor) ? 'for today' : '',
      // A card that grants a buy SAYS so on its face (Matt: "there's no
      // indication on the elders speak that it has this ability").
      c.buys ? `+${c.buys} buy` : '',
      c.law ? `law, for good: ${lawText(id)}` : '',
      // A grievance: what playing it does, in four words (playtest 14).
      c.grievance ? 'settles it; gives nothing' : '',
      // The Charter said only "law" (playtest 7: "at a minimum, that card
      // should explain itself").
      c.charter ? 'a grown village becomes a town' : '',
      c.envoy ? 'a neighbouring people warms to you one step' : '',
      c.adds ? 'adds a card to the deck' : '',
      // The one pair-up, as the card says it (playtest 14).
      c.with ? c.with.text : '',
    ].filter(Boolean),
    push,
    toRole: Boolean(gives),
    bonuses,
  };
};
